import React, { useEffect } from 'react';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import type { Session } from 'next-auth';
import Router from 'next/router';
import NProgress from 'nprogress';
import { SessionProvider } from 'next-auth/react';
import { SessionErrorHandler } from '../components/SessionErrorHandler';
import { Inter } from 'next/font/google';
import { atom, useAtom, useAtomValue } from 'jotai';

// https://fontawesome.com/v5/docs/web/use-with/react#next-js
import { config } from '@fortawesome/fontawesome-svg-core';
import '../styles/globals.css';
import { LoadingOverlay } from '@/components/LoadingOverlay';
import { logoUrlAtom } from '@/stores/logoAtom'; 
config.autoAddCss = false;

const inter = Inter({ subsets: ['latin'] });

type NextPageWithLayout = NextPage & {
    layout?: (page: React.ReactElement) => React.ReactNode;
}

type AppPropsWithLayout = AppProps<{
    session?: Session;
}> & {
    Component: NextPageWithLayout;
}

export const loadingOverlayAtom = atom<boolean>(false)

function DynamicFavicon() {
  const faviconUrl = useAtomValue(logoUrlAtom);

  useEffect(() => {
    let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");

    if (link) {
      link.href = faviconUrl;
    } else {
      link = document.createElement('link');
      link.rel = 'icon';
      link.href = faviconUrl;
      document.head.appendChild(link);
    }
  }, [faviconUrl]);

  return null;
}

function CustomApp({
    Component,
    pageProps: { session, ...pageProps }
}: AppPropsWithLayout): JSX.Element {
    // https://nextjs.org/docs/basic-features/layouts#per-page-layouts
    const withLayout = Component.layout ?? (page => page);
    const [isLoading] = useAtom(loadingOverlayAtom)

    return (
        // https://next-auth.js.org/getting-started/client#sessionprovider
        <SessionProvider session={session} refetchWhenOffline={false} refetchOnWindowFocus={false} refetchInterval={120}>
            <SessionErrorHandler>
                <main className={inter.className}>
                        <DynamicFavicon />
                        <LoadingOverlay isLoading={isLoading}/>
                        {withLayout(<Component {...pageProps} />)}
                </main>
            </SessionErrorHandler>
        </SessionProvider>
    );
}

NProgress.configure({
    showSpinner: false
});

Router.events.on('routeChangeStart', NProgress.start);
Router.events.on('routeChangeComplete', NProgress.done);
Router.events.on('routeChangeError', NProgress.done);

export default CustomApp;
